import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, Search, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function VerifyCertificate() {
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [state, setState] = useState("idle"); // idle | checking | valid | invalid
  const [cert, setCert] = useState(null);

  const verify = async (raw) => {
    const c = (raw ?? code).trim().toUpperCase();
    if (!c) return;
    setState("checking");
    setParams({ code: c });
    try {
      const { data } = await api.get(`/certificates/${encodeURIComponent(c)}`);
      setCert(data); setState("valid");
    } catch (e) {
      const status = e?.response?.status;
      if (status && status >= 500) { setCert(null); setState("error"); }
      else if (!status) { setCert(null); setState("error"); }
      else { setCert(null); setState("invalid"); }
    }
  };

  useEffect(() => {
    if (params.get("code")) verify(params.get("code"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto max-w-xl px-5 py-16 lg:py-24" data-testid="verify-page">
      <div className="text-center">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--evergreen-soft)] text-[color:var(--evergreen)]"><ShieldCheck size={26} /></span>
        <h1 className="mt-5 font-editorial text-3xl font-semibold text-[color:var(--ink)] sm:text-4xl">Verify a certificate</h1>
        <p className="mt-3 text-sm text-[color:var(--ink-body)]">Paste a certificate ID to confirm it was genuinely issued by us.</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); verify(); }} className="mt-8 flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. A523525484" data-testid="verify-input"
          className="flex-1 rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-sm uppercase tracking-wide text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
        <button type="submit" disabled={state === "checking"} data-testid="verify-submit" className="btn-edge px-5">
          {state === "checking" ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Verify
        </button>
      </form>

      {state === "valid" && cert && (
        <div className="mt-8 overflow-hidden rounded-xl border-2 border-[color:var(--evergreen)] bg-[color:var(--paper)] p-6" data-testid="verify-valid">
          <div className="flex items-center gap-2 text-[color:var(--evergreen)]"><ShieldCheck size={18} /><p className="font-semibold">Genuine certificate</p></div>
          <div className="mt-4 grid gap-2 text-sm">
            <Row label="Recipient" value={cert.user_name} />
            <Row label="Course" value={cert.course_title} />
            <Row label="Issued by" value={cert.site_name} />
            {cert.instructor_name && <Row label="Instructor" value={cert.instructor_name} />}
            <Row label="Issued" value={cert.issued_at ? new Date(cert.issued_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—"} />
            <Row label="Certificate ID" value={cert.code} mono />
          </div>
          <Link to={`/certificate/${cert.code}`} data-testid="verify-open" className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[color:var(--evergreen)] hover:underline">
            Open full certificate <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {state === "invalid" && (
        <div className="mt-8 rounded-xl border border-[color:var(--alert)] bg-[color:var(--alert)]/5 p-6 text-center" data-testid="verify-invalid">
          <ShieldX size={22} className="mx-auto text-[color:var(--alert)]" />
          <p className="mt-3 font-semibold text-[color:var(--ink)]">No matching certificate</p>
          <p className="mt-1 text-sm text-[color:var(--ink-body)]">This ID is not genuine, or the certificate has been revoked. Double-check the ID and try again.</p>
        </div>
      )}

      {state === "error" && (
        <div className="mt-8 rounded-xl border border-[color:var(--ink-hairline)] bg-white p-6 text-center" data-testid="verify-error">
          <p className="font-semibold text-[color:var(--ink)]">Couldn't reach the verification service</p>
          <p className="mt-1 text-sm text-[color:var(--ink-body)]">Something went wrong on our end — please try again in a moment.</p>
          <button onClick={() => verify()} className="btn-edge mt-4 text-sm" data-testid="verify-retry">Retry</button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[color:var(--ink-hairline)] pb-2 last:border-0">
      <span className="text-[color:var(--ink-muted)]">{label}</span>
      <span className={`text-right text-[color:var(--ink)] ${mono ? "font-mono-edge" : ""}`}>{value}</span>
    </div>
  );
}
